'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Card, CardContent, CardFooter } from '@ahafood/ui/components/card';
import { Progress } from '@ahafood/ui/components/progress';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { type Address, assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Clock, Gift, MapPin, Zap } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  type Progress as ApiProgress,
  getDealProgressFn,
  type ProgressDeal,
} from '@/api/use-get-deal-progress';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { LoginDialog } from '@/app/[locale]/(main)/store/components/fb-login';
import { ResultBlock } from '@/components/result-block';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { useIsMobile } from '@/lib/is-mobile';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import type { MenuItem } from '@/types/item';

type StoreDealProgressProps = {
  data: ApiProgress[];
  payload: {
    city_id: string;
    lat?: number;
    lng?: number;
    reach_max_use?: boolean;
  };
};

const getTimeRange = (store_open_hours: any) => {
  if (!store_open_hours) return '06:00 - 22:00';

  const today = new Date().getDay();
  const dayKey = today === 0 ? '6' : (today - 1).toString();

  const todayHours = store_open_hours[dayKey];
  if (!todayHours || todayHours.length === 0) return 'Đóng cửa';

  const firstSlot = todayHours[0];
  const lastSlot = todayHours[todayHours.length - 1];

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  return `${formatTime(firstSlot.start_time)} - ${formatTime(lastSlot.end_time)}`;
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

const renderDealBadge = (dealItem: ApiProgress) => {
  const { discount_config } = dealItem;
  let badgeText = 'Deal';

  if (discount_config.type === 'VALUE') {
    badgeText = `Giảm ${discount_config.value.toLocaleString()}đ`;
  } else if (discount_config.type === 'FLAT') {
    badgeText = `Đồng giá ${discount_config.value.toLocaleString()}đ`;
  } else if (discount_config.type === 'PERCENT') {
    badgeText = `Giảm ${discount_config.value}%`;
  }

  return (
    <Badge className="absolute top-3 md:left-3 left-2 z-10 bg-gradient-to-r from-red-40 to-primary text-white font-bold md:px-3 px-1.5 md:py-1.5 py-0.5 text-xs shadow-lg rounded-full">
      ⚡ {badgeText}
    </Badge>
  );
};

// Optimized DealCard Component with unified responsive design
const DealCard = ({
  dealItem,
  index,
  isMobile,
  onSendMessage,
  onDealToggle,
}: {
  dealItem: ApiProgress;
  index: number;
  isMobile: boolean;
  onSendMessage: (item: ApiProgress) => void;
  onDealToggle: (item: ApiProgress, status: string) => void;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const status = dealItem.status;
  const remaining =
    dealItem.max_use === -1 ? -1 : dealItem.max_use - dealItem.usage_count;
  const timeRange = getTimeRange(dealItem.store_open_hours);
  const isUnlimited = dealItem.max_use === -1;

  // Unified render logic with responsive design
  return (
    <motion.div
      custom={index}
      initial={!isMobile && index < 8 ? 'hidden' : false}
      animate={!isMobile && index < 8 ? 'visible' : false}
      variants={!isMobile && index < 8 ? cardVariants : undefined}
      whileHover={
        !isMobile ? { scale: 1.03, transition: { duration: 0.2 } } : undefined
      }
      whileTap={!isMobile ? { scale: 0.97 } : undefined}
      className="group cursor-pointer h-full"
      onClick={() => onDealToggle(dealItem, dealItem.status)}
    >
      <Card
        className={cn(
          'overflow-hidden h-full border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white rounded-2xl',
          isMobile ? 'flex flex-row' : 'flex flex-col'
        )}
      >
        <div
          className={cn(
            'relative overflow-hidden bg-gradient-to-br from-neutral-10 to-neutral-20',
            isMobile ? 'aspect-square h-full w-36 shrink-0' : 'h-48 flex-none'
          )}
        >
          {renderDealBadge(dealItem)}

          {dealItem.total_items && dealItem.total_items > 1 && (
            <Badge
              className={cn(
                'absolute z-10 bg-yellow-10 hover:bg-yellow-10 backdrop-blur-sm text-primary-60 font-semibold shadow-md rounded-full border border-primary-30',
                isMobile
                  ? 'top-10 left-1 px-1 py-0.5 text-xs'
                  : 'top-3 right-3 px-2 py-1 text-xs'
              )}
            >
              <Gift
                className={cn(isMobile ? 'size-2.5 mr-0.5' : 'size-3 mr-1')}
              />
              +{dealItem.total_items - 1}
              {!isMobile && ' món'}
            </Badge>
          )}

          <Avatar className="size-full rounded-none bg-white flex items-center justify-center overflow-hidden">
            <AvatarImage
              src={dealItem.item_image_url || dealItem.store_image_url || ''}
              alt={dealItem.item_name || 'Deal image'}
              className="size-full transition-transform duration-300 group-hover:scale-105 object-cover"
            />
            <AvatarFallback className="size-full">
              {dealItem.item_name ? dealItem.item_name.charAt(0) : 'D'}
            </AvatarFallback>
          </Avatar>

          <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />

          {(status === 'INACTIVE' || status === 'OUT_OF_STOCK') && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
              <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg">
                <p className="text-neutral-70 font-bold text-sm">
                  {isMobile ? 'Hết deal' : 'Đã hết deal'}
                </p>
              </div>
            </div>
          )}
        </div>

        <CardContent
          className={cn(
            'flex flex-col justify-between',
            isMobile ? 'p-2.5 flex-1 min-w-0' : 'p-4 flex-1'
          )}
        >
          <div className={cn(isMobile ? 'mb-2' : 'mb-3')}>
            <h2
              className={cn(
                'font-bold text-neutral-90 truncate',
                isMobile ? 'text-sm mb-1' : 'text-lg mb-2'
              )}
            >
              {dealItem.item_name}
            </h2>

            <div
              className={cn(
                'flex items-center text-neutral-60',
                isMobile ? 'text-xs mb-1' : 'text-sm mb-2'
              )}
            >
              <MapPin
                className={cn(
                  'flex-none text-primary-50',
                  isMobile ? 'size-3 mr-1' : 'size-4 mr-1.5'
                )}
              />
              <span className="truncate font-medium">
                {dealItem.distance && dealItem.distance > 0 && (
                  <span className="text-primary-50">{`${(dealItem.distance / 1000).toFixed(1)}km`}</span>
                )}{' '}
                {dealItem.store_name}
              </span>
            </div>

            <div
              className={cn(
                'flex items-center text-neutral-50',
                isMobile ? 'text-xs' : 'text-sm'
              )}
            >
              <Clock
                className={cn(isMobile ? 'size-3 mr-1' : 'size-4 mr-1.5')}
              />
              <span className={!isMobile ? 'font-medium' : ''}>
                {timeRange}
              </span>
            </div>
          </div>

          {/* Progress and Action Section */}
          <div className={cn(isMobile ? 'space-y-2' : 'space-y-3')}>
            <div className="relative">
              {isUnlimited ? (
                <div className={cn('relative', isMobile ? 'mb-1' : 'mb-2')}>
                  <Progress
                    value={50}
                    className={cn(isMobile ? 'h-1.5' : 'h-3', 'bg-neutral-20')}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 z-10 transition-all duration-300 ease-out"
                    style={{ left: `calc(50% - 6px)` }}
                  >
                    <Zap className="size-3 fill-yellow-20 text-yellow-30" />
                  </div>
                </div>
              ) : (
                <div className={cn('relative', isMobile ? 'mb-1' : 'mb-2')}>
                  <Progress
                    value={Math.max(
                      5,
                      ((dealItem.max_use - remaining) / dealItem.max_use) * 100
                    )}
                    className={cn(isMobile ? 'h-1.5' : 'h-3', 'bg-neutral-20')}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 z-10 transition-all duration-300 ease-out"
                    style={{
                      left: `calc(${Math.max(5, ((dealItem.max_use - remaining) / dealItem.max_use) * 100)}% - 6px)`,
                    }}
                  >
                    <Zap className="size-3 fill-yellow-20 text-yellow-30" />
                  </div>
                </div>
              )}
            </div>

            <div
              className={cn(
                'flex items-center justify-between',
                isMobile ? 'text-xs' : 'text-sm'
              )}
            >
              <span
                className={cn(
                  'font-semibold text-primary-50',
                  !isMobile && 'text-base'
                )}
              >
                {isUnlimited
                  ? isMobile
                    ? 'Hot 🔥'
                    : 'Đang hot 🔥'
                  : isMobile
                    ? `${remaining}/${dealItem.max_use}`
                    : `Còn ${remaining}/${dealItem.max_use} đơn`}
              </span>
            </div>

            {dealItem.discounted_price ? (
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'text-primary font-bold',
                    isMobile ? 'text-sm' : 'text-xl'
                  )}
                >
                  {dealItem.discounted_price.toLocaleString()}đ
                </span>
                <span
                  className={cn(
                    'line-through text-neutral-40 font-semibold',
                    isMobile ? 'text-xs' : 'text-sm'
                  )}
                >
                  {dealItem.price.toLocaleString()}đ
                </span>
              </div>
            ) : (
              <div
                className={cn('font-bold', isMobile ? 'text-sm' : 'text-lg')}
              >
                {dealItem.price.toLocaleString()}đ
              </div>
            )}
          </div>
          {isMobile && (
            <div className="flex items-end pt-2">
              <Button
                type="button"
                className={cn(
                  'w-full font-bold h-8 text-xs transition-all duration-300 rounded-lg',
                  'bg-gradient-to-r from-primary to-red-40 hover:from-primary-60 hover:to-red-60 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  void onSendMessage(dealItem);
                }}
              >
                {status === 'OUT_OF_STOCK' || status === 'INACTIVE' ? (
                  <>
                    <MapPin className="size-3 mr-1" />
                    Xem cửa hàng
                  </>
                ) : (
                  <>
                    <Zap className="size-3 mr-1" />
                    Đặt ngay
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>

        {!isMobile && (
          <CardFooter className="px-4 pb-4 pt-0">
            <Button
              type="button"
              className={cn(
                'w-full font-bold h-12 text-sm transition-all duration-300 rounded-xl',
                'bg-gradient-to-r from-primary to-red-40 hover:from-primary-60 hover:to-red-60 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
              )}
              onClick={(e) => {
                e.stopPropagation();
                void onSendMessage(dealItem);
              }}
            >
              {status === 'OUT_OF_STOCK' || status === 'INACTIVE' ? (
                <>
                  <MapPin className="size-5 mr-2" />
                  Xem cửa hàng
                </>
              ) : (
                <>
                  <Zap className="size-5 mr-2" />
                  Đặt ngay
                </>
              )}
            </Button>
          </CardFooter>
        )}
      </Card>
    </motion.div>
  );
};

export default function StoreDealProgress({
  data,
  payload,
}: StoreDealProgressProps) {
  const limit = 20;
  const routes = useCityRoutes();
  const { isAuthenticated } = useAuthStore();
  const address = useAiFoodOrderStore.use.address() as Address | null;
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  // const [selectedDealForStoreInfo, setSelectedDealForStoreInfo] =
  //   useState<ApiProgress | null>(null);
  // const [isStoreInfoOpen, setIsStoreInfoOpen] = useState(false);
  const goToOrderWebstore = useGoToOrderWebstore();
  const router = useRouter();
  const isMobile = useIsMobile();

  const {
    data: dealsData,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['deals', payload],
    queryFn: async ({ pageParam = 0 }) => {
      return getDealProgressFn({
        ...payload,
        limit,
        skip: pageParam,
        ...(address?.lat && address?.lng
          ? { lat: address.lat, lng: address.lng }
          : {}),
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.progress) return undefined;
      const loaded = allPages.reduce(
        (acc, page) => acc + (page?.progress?.length || 0),
        0
      );
      return lastPage.progress.length < limit ? undefined : loaded;
    },
    initialData: data?.length
      ? {
          pages: [{ progress: data } as ProgressDeal],
          pageParams: [0],
        }
      : undefined,
  });

  const deals = useMemo(() => {
    return (
      dealsData?.pages?.flatMap((page) => page.progress).filter(Boolean) ?? []
    );
  }, [dealsData?.pages]);

  const convertToMenuItem = useCallback((dealItem: ApiProgress): MenuItem => {
    return {
      _id: dealItem.item_id,
      name: dealItem.item_name,
      price: dealItem.price,
      discounted_price: dealItem.discounted_price,
      image_thumb_url: dealItem.item_image_url || '',
    };
  }, []);

  const handleDealToggle = (dealItem: ApiProgress, status: string) => {
    if (status) {
      router.push(routes.storeDetail(dealItem.store_slug));
    }
  };

  const handleSendMessageWithFallback = useCallback(
    async (dealItem: ApiProgress) => {
      const status = dealItem.status;
      if (status === 'OUT_OF_STOCK' || status === 'INACTIVE') {
        return;
      }
      if (!isAuthenticated) {
        setShowLoginDialog(true);
        return;
      }

      await goToOrderWebstore(
        dealItem.page_id.toString(),
        dealItem.store_slug,
        dealItem.nearest_branch?.id ?? '',
        convertToMenuItem(dealItem),
        dealItem.store_id.toString()
      );
      router.push(routes.storeDetail(dealItem.store_slug));
    },
    [isAuthenticated, goToOrderWebstore, convertToMenuItem, router, routes]
  );

  if (!deals || deals.length === 0) {
    return (
      <div className="min-h-[50svh] flex items-center justify-center">
        <ResultBlock
          className="min-h-[50svh]"
          iconUrl={`${assetPrefix}/static/images/store-empty.webp`}
          title="Không có deals nào"
          description="Hiện tại không có deals nào đang diễn ra tại khu vực của bạn"
        />
      </div>
    );
  }

  return (
    <>
      <InfiniteScroll
        className={cn(
          'grid auto-rows-max items-stretch justify-items-stretch pb-20',
          isMobile
            ? 'gap-4 grid-cols-1'
            : 'gap-6 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 px-2 xl:px-0'
        )}
        dataLength={deals.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<LoadingSkeleton />}
        endMessage={
          <p className="col-span-full text-center py-4 text-neutral-40"></p>
        }
      >
        {deals.map((dealItem, index) => (
          <DealCard
            key={`deal-${dealItem.item_id}-${dealItem.store_id}-${index}`}
            dealItem={dealItem}
            index={index}
            isMobile={isMobile}
            onSendMessage={handleSendMessageWithFallback}
            onDealToggle={handleDealToggle}
          />
        ))}
      </InfiniteScroll>
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="deal_progress"
      />
    </>
  );
}

// Skeleton loader for infinite scroll
function LoadingSkeleton() {
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        'col-span-full grid auto-rows-max items-stretch justify-items-stretch',
        isMobile
          ? 'gap-4 grid-cols-1'
          : 'gap-6 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 px-2 xl:px-0'
      )}
    >
      {[...Array(4)].map((_, index) => (
        <Skeleton
          key={index}
          className={cn(
            'rounded-2xl bg-neutral-10 animate-pulse',
            isMobile ? 'h-36 w-full' : 'h-48 w-full'
          )}
        />
      ))}
    </div>
  );
}
